import { useMemo, useState } from 'react';
import { useGameContext } from '@/state/GameContext';
import { itemRegistry } from '@/engine/VanillaItemRegistry';
import type { ItemCategory, ItemId } from '@/types/domain';
import { ItemIcon } from './ItemIcon';

const CATEGORIES: Array<ItemCategory | 'all'> = [
  'all', 'tool', 'weapon', 'armor', 'block', 'material', 'food', 'transport',
  'redstone', 'machine', 'container', 'utility', 'decorative', 'mob', 'misc',
];

type Source = 'vanilla' | 'discovered' | 'favorites' | 'all';

export function ItemBrowser() {
  const game = useGameContext();
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState<ItemCategory | 'all'>('all');
  const [source, setSource] = useState<Source>('vanilla');

  const ids: ItemId[] = useMemo(() => {
    let base: ItemId[];
    if (source === 'discovered') base = game.discovered.map((d) => d.id);
    else if (source === 'favorites') base = game.favorites;
    else if (source === 'all') {
      const van = itemRegistry.allVanilla().map((i) => i.id);
      base = [...game.discovered.map((d) => d.id), ...van];
    } else base = itemRegistry.allVanilla().map((i) => i.id);

    const q = query.trim().toLowerCase();
    return base.filter((id) => {
      const m = game.metaFor(id);
      if (!m) return false;
      if (cat !== 'all' && m.category !== cat) return false;
      if (!q) return true;
      return (
        m.name.toLowerCase().includes(q) ||
        id.toLowerCase().includes(q) ||
        m.tags.some((t) => t.includes(q)) ||
        (m.aliases || []).some((a) => a.includes(q))
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, query, cat, game.discovered, game.favorites, game.metaFor]);

  const shown = ids.slice(0, 600);

  return (
    <div className="panel">
      <div className="panel-head">Items</div>
      <div className="panel-body">
        <input
          className="search"
          placeholder="Search items / tags..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search items"
        />
        <div className="chips" role="tablist" aria-label="Source">
          {(['vanilla', 'discovered', 'favorites', 'all'] as Source[]).map((s) => (
            <button key={s} className={`chip${source === s ? ' active' : ''}`} onClick={() => setSource(s)}>
              {s}
            </button>
          ))}
        </div>
        <div className="chips" aria-label="Category filter">
          {CATEGORIES.map((c) => (
            <button key={c} className={`chip${cat === c ? ' active' : ''}`} onClick={() => setCat(c)}>
              {c}
            </button>
          ))}
        </div>
        <div className="muted" style={{ margin: '4px 0 8px' }}>
          {ids.length} item{ids.length === 1 ? '' : 's'}{shown.length < ids.length ? ` (showing ${shown.length})` : ''}
        </div>
        <div className="browser-grid">
          {shown.map((id) => (
            <ItemIcon
              key={id}
              itemId={id}
              size={40}
              draggable
              favorite={game.favorites.includes(id)}
              onClick={() => game.placeItemAnySlot(id)}
            />
          ))}
          {shown.length === 0 ? <div className="muted">No items match.</div> : null}
        </div>
      </div>
    </div>
  );
}
